import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PageHeader } from "../../components/ui";
import { listWorkCenters } from "../../api/workCenters";
import { listFormingOperators } from "../../api/users";
import {
  getAllQueues,
  getAvailableOperations,
  getWorkCenterQueue,
  saveWorkCenterQueue,
} from "../../api/workCenterQueue";

const STATUS_LABELS = {
  planned: "Plana Alındı",
  started: "Başladı",
  finished: "Tamamlandı",
};
const STATUS_COLORS = {
  planned: "warning",
  started: "warning",
  finished: "success",
};
const PAGE_SIZE = 10;

function ListPager({ page, total, onChange }) {
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  if (pageCount <= 1) return null;

  return (
    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" mt={1.5}>
      <Button size="small" disabled={page <= 0} onClick={() => onChange(page - 1)}>
        ← Önceki
      </Button>
      <Typography variant="caption" color="text.secondary">
        Sayfa {page + 1} / {pageCount}
      </Typography>
      <Button size="small" disabled={page >= pageCount - 1} onClick={() => onChange(page + 1)}>
        Sonraki →
      </Button>
    </Stack>
  );
}

function isCuttingCode(code) {
  const c = String(code || "").toLowerCase();
  return c.includes("kesim") || c.includes("cut") || c === "ksm";
}

function toTs(v) {
  const t = new Date(v || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

function getQueueStatusChipProps(item) {
  if (item?._pending_add) {
    return {
      label: "Bekliyor",
      color: "warning",
      sx: {
        bgcolor: "#FDE68A",
        color: "#92400E",
        fontWeight: 700,
      },
    };
  }

  const status = String(item?.op_status || "");
  if (status === "planned") {
    return {
      label: STATUS_LABELS.planned,
      color: "warning",
      sx: {
        bgcolor: "#FB923C",
        color: "#7C2D12",
        fontWeight: 700,
      },
    };
  }

  return {
    label: STATUS_LABELS[status] || status,
    color: STATUS_COLORS[status] || "default",
    sx: {},
  };
}

export default function WorkCenterPlanningPage() {
  const [workCenters, setWorkCenters] = useState([]);
  const [selectedWCId, setSelectedWCId] = useState("");
  const [queue, setQueue] = useState([]);
  const [available, setAvailable] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [formingOperators, setFormingOperators] = useState([]);
  const [queuePage, setQueuePage] = useState(0);
  const [availablePage, setAvailablePage] = useState(0);
  // Yalnızca "Eklenebilir İşler" listesini filtreler; kuyruk etkilenmez.
  const [availableSearch, setAvailableSearch] = useState("");

  // Filtre değişince ilk sayfaya dön; aksi halde boş sayfada kalınabilir.
  useEffect(() => {
    setAvailablePage(0);
  }, [availableSearch]);

  useEffect(() => {
    listWorkCenters()
      .then((items) => setWorkCenters(items || []))
      .catch(() => setError("İş merkezleri yüklenemedi."));
    listFormingOperators()
      .then((items) => setFormingOperators(items || []))
      .catch(() => setError("FRM operatörleri yüklenemedi."));
    loadAllQueues();
  }, []);

  async function loadAllQueues() {
    try {
      const data = await getAllQueues();
      setAllQueues(data || []);
    } catch {
      // ignore
    }
  }

  const loadQueueAndAvailable = useCallback(async (wcId) => {
    if (!wcId) return;
    setLoading(true);
    setError("");
    try {
      const [q, av] = await Promise.all([
        getWorkCenterQueue(wcId),
        getAvailableOperations(wcId),
      ]);
      setQueue(q || []);
      setAvailable(av || []);
      setQueuePage(0);
      setAvailablePage(0);
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Kuyruk yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  function handleWCChange(e) {
    const id = e.target.value;
    setSelectedWCId(id);
    setQueue([]);
    setAvailable([]);
    setQueuePage(0);
    setAvailablePage(0);
    setAvailableSearch("");
    setSuccess("");
    setError("");
    if (id) loadQueueAndAvailable(id);
  }

  function handleAdd(op) {
    const nextOrder = queue.length + 1;
    setQueue((prev) => [
      ...prev,
      {
        production_order_operation_id: op.production_order_operation_id,
        priority_order: nextOrder,
        op_status: op.op_status,
        op_no: op.op_no,
        operation_code: op.operation_code,
        op_description: op.op_description,
        order_no: op.order_no,
        customer_code: op.customer_code,
        customer_name: op.customer_name,
        customer_product_code: op.customer_product_code,
        order_status: op.order_status,
        product_name: op.product_name,
        product_code: op.product_code,
        order_product_name: op.order_product_name,
        product_variant_name: op.product_variant_name,
        order_has_print_routing: op.order_has_print_routing,
        operator_user_id: null,
        operator_name: null,
        operator_employee_code: null,
        planned_qty: op.planned_qty,
        qty_target: op.qty_target,
        qty_done: op.qty_done,
        _pending_add: true,
      },
    ]);
    setAvailable((prev) =>
      prev.filter((x) => x.production_order_operation_id !== op.production_order_operation_id)
    );
  }

  function handleRemove(idx) {
    const removed = queue[idx];
    setAvailable((prev) => [
      ...prev,
      {
        production_order_operation_id: removed.production_order_operation_id,
        op_status: removed.op_status,
        op_no: removed.op_no,
        operation_code: removed.operation_code,
        op_description: removed.op_description,
        order_no: removed.order_no,
        customer_code: removed.customer_code,
        customer_name: removed.customer_name,
        customer_product_code: removed.customer_product_code,
        order_status: removed.order_status,
        product_name: removed.product_name,
        product_code: removed.product_code,
        order_product_name: removed.order_product_name,
        product_variant_name: removed.product_variant_name,
        order_has_print_routing: removed.order_has_print_routing,
        planned_qty: removed.planned_qty,
        qty_target: removed.qty_target,
        qty_done: removed.qty_done,
      },
    ]);
    setQueue((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((item, i) => ({ ...item, priority_order: i + 1 }));
    });
  }

  function handleMoveUp(idx) {
    if (idx === 0) return;
    setQueue((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next.map((item, i) => ({ ...item, priority_order: i + 1 }));
    });
  }

  function handleMoveDown(idx) {
    setQueue((prev) => {
      if (idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next.map((item, i) => ({ ...item, priority_order: i + 1 }));
    });
  }

  function handleOperatorChange(operationId, value) {
    const operatorUserId = value === "" ? null : Number(value);
    setQueue((prev) =>
      prev.map((item) =>
        item.production_order_operation_id === operationId
          ? {
              ...item,
              operator_user_id: operatorUserId,
              operator_name:
                formingOperators.find((operator) => Number(operator.id) === operatorUserId)?.name || null,
              operator_employee_code:
                formingOperators.find((operator) => Number(operator.id) === operatorUserId)?.employee_code || null,
            }
          : item
      )
    );
  }

  async function handleSave() {
    if (!selectedWCId) return;
    if (
      selectedWCIsForming &&
      queue.some(
        (item) =>
          !Number.isInteger(Number(item.operator_user_id)) || Number(item.operator_user_id) <= 0
      )
    ) {
      setError("FRM iş merkezindeki tüm işler için operatör seçin.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await saveWorkCenterQueue(
        Number(selectedWCId),
        queue.map((item) => ({
          production_order_operation_id: item.production_order_operation_id,
          priority_order: item.priority_order,
          operator_user_id: selectedWCIsForming ? item.operator_user_id : null,
        }))
      );
      setQueue((prev) =>
        prev.map((item) =>
          item._pending_add
            ? { ...item, _pending_add: false, op_status: "planned" }
            : item
        )
      );
      setSuccess("Kuyruk kaydedildi.");
      await loadQueueAndAvailable(Number(selectedWCId));
      await loadAllQueues();
    } catch (err) {
      setError(err?.response?.data?.error?.message || "Kayıt başarısız.");
    } finally {
      setSaving(false);
    }
  }

  const selectedWC = workCenters.find((wc) => String(wc.id) === String(selectedWCId));
  const selectedWCIsForming = String(selectedWC?.group_code || "").toUpperCase() === "FRM";
  const selectedWCIsCutting = isCuttingCode(selectedWC?.name || "");
  const queuePageCount = Math.max(1, Math.ceil(queue.length / PAGE_SIZE));
  const currentQueuePage = Math.min(queuePage, queuePageCount - 1);
  const visibleQueue = queue
    .slice(currentQueuePage * PAGE_SIZE, (currentQueuePage + 1) * PAGE_SIZE)
    .map((item, index) => ({ item, idx: currentQueuePage * PAGE_SIZE + index }));

  const filteredAvailable = useMemo(() => {
    const q = availableSearch.trim().toLocaleLowerCase("tr");
    if (!q) return available;
    return available.filter((op) => {
      const orderNo = String(op.order_no || "").toLocaleLowerCase("tr");
      const customerCode = String(op.customer_code || "").toLocaleLowerCase("tr");
      return orderNo.includes(q) || customerCode.includes(q);
    });
  }, [available, availableSearch]);

  const availablePageCount = Math.max(1, Math.ceil(filteredAvailable.length / PAGE_SIZE));
  const currentAvailablePage = Math.min(availablePage, availablePageCount - 1);
  const visibleAvailable = filteredAvailable.slice(
    currentAvailablePage * PAGE_SIZE,
    (currentAvailablePage + 1) * PAGE_SIZE
  );
  const selectedWCReport = (allQueues || []).find(
    (wc) => String(wc.work_center_id) === String(selectedWCId)
  );
  const selectedReportItems = (selectedWCReport?.items || [])
    .slice()
    .sort((a, b) => toTs(b.changed_at) - toTs(a.changed_at))
    .slice(0, 10);
  const allQueuesLimited = (allQueues || []).map((wc) => ({
    ...wc,
    items: (wc.items || [])
      .slice()
      .sort((a, b) => toTs(b.changed_at) - toTs(a.changed_at))
      .slice(0, 5),
  }));

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 1540, mx: "auto", "& .MuiOutlinedInput-root": { borderRadius: 2.25, bgcolor: "#fff" } }}>
      <PageHeader
        title="İş Merkezi Planlama"
        subtitle="İş merkezlerine iş emirleri atayın ve öncelik sırasını belirleyin."
        actions={<Paper elevation={0} sx={{ px: 1.5, py: 1, borderRadius: 3, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe" }}><Typography variant="body2" sx={{ color: "#3730a3", fontWeight: 800 }}>{selectedWC ? selectedWC.name : "İş merkezi seçiniz"}</Typography></Paper>}
      />

      {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess("")}>{success}</Alert>}

      {/* İş Merkezi Seçimi */}
      <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe", boxShadow: "0 12px 30px rgba(79, 70, 229, 0.10)" }}>
        <Typography variant="subtitle1" fontWeight={900} color="#312e81" sx={{ mb: 1.25 }}>İş Merkezi Seçimi</Typography>
        <FormControl sx={{ minWidth: { xs: "100%", sm: 360 } }}>
          <InputLabel>İş Merkezi Seç</InputLabel>
          <Select value={selectedWCId} label="İş Merkezi Seç" onChange={handleWCChange}>
            <MenuItem value="">
              <em>Seçiniz...</em>
            </MenuItem>
            {workCenters.map((wc) => (
              <MenuItem key={wc.id} value={String(wc.id)}>
                {wc.name}{wc.code ? ` (${wc.code})` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {selectedWCIsCutting && (
          <Alert severity="info" sx={{ mt: 1, py: 0 }}>
            Kesim iş merkezinde sıra zorunluluğu uygulanmaz — operatör istediği işe başlayabilir.
          </Alert>
        )}
      </Paper>

      {/* Eklenebilir İşler Filtresi — yalnızca sağdaki listeyi süzer */}
      {selectedWCId && (
        <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe", boxShadow: "0 12px 30px rgba(79, 70, 229, 0.08)" }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ xs: "stretch", sm: "center" }}>
            <Typography variant="subtitle1" fontWeight={900} color="#312e81" sx={{ whiteSpace: "nowrap" }}>
              Eklenebilir İşlerde Ara
            </Typography>
            <TextField
              size="small"
              placeholder="İş emri no veya müşteri kodu"
              value={availableSearch}
              onChange={(e) => setAvailableSearch(e.target.value)}
              sx={{ minWidth: { sm: 320 } }}
              InputProps={{
                endAdornment: availableSearch ? (
                  <IconButton size="small" onClick={() => setAvailableSearch("")} aria-label="Aramayı temizle">
                    <span style={{ fontSize: 16, lineHeight: 1 }}>×</span>
                  </IconButton>
                ) : null,
              }}
            />
            {availableSearch ? (
              <Typography variant="body2" color="text.secondary">
                {filteredAvailable.length} / {available.length} iş
              </Typography>
            ) : null}
          </Stack>
        </Paper>
      )}

      {/* İki Panel: Kuyruk + Eklenebilir */}
      {selectedWCId && (
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="flex-start">
         {/* Sol: Mevcut Kuyruk */}
          <Paper elevation={0} sx={{ flex: 1, p: { xs: 1.5, md: 2.25 }, minWidth: 0, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe", boxShadow: "0 12px 30px rgba(79, 70, 229, 0.08)" }}>
            <Typography variant="subtitle1" fontWeight={900} color="#312e81" mb={1.5}>
              Kuyruk ({queue.length} iş)
            </Typography>
            {loading && (
              <Typography variant="body2" color="text.secondary">Yükleniyor...</Typography>
            )}
            {!loading && queue.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Henüz atanmış iş yok. Sağ panelden ekleyin.
              </Typography>
            )}
            <Stack spacing={1}>
              {visibleQueue.map(({ item, idx }) => {
                const statusChip = getQueueStatusChipProps(item);
                return (
                <Box
                  key={item.production_order_operation_id}
                  sx={{
                    border: "1px solid",
                    borderColor: "#ddd6fe",
                    borderRadius: 2,
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: item.op_status === "finished" ? "#f0fdf4" : "#fff",
                  }}
                >
                  {/* Öncelik numarası */}
                  <Box
                    sx={{
                      minWidth: 32,
                      height: 32,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: 700,
                      fontSize: 14,
                      flexShrink: 0,
                    }}
                  >
                    {idx + 1}
                  </Box>

                  {/* Bilgi */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {item.customer_code || "—"} — {item.customer_name || "—"} — {item.order_no || "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {item.product_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      Müşteri Ürün Kodu: {item.customer_product_code || "—"}
                    </Typography>
                    <Stack direction="row" spacing={0.5} mt={0.3} flexWrap="wrap">
                      <Chip
                        label={item.operation_code || `OP${item.op_no}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 12, height: 24 }}
                      />
                      <Chip
                        label={statusChip.label}
                        color={statusChip.color}
                        size="small"
                        sx={{ fontSize: 12, height: 24, ...statusChip.sx }}
                      />
                      {isCuttingCode(item.operation_code) && (
                        <Chip
                          label="sıra serbest"
                          size="small"
                          color="info"
                          sx={{ fontSize: 12, height: 24 }}
                        />
                      )}
                    </Stack>
                  </Box>

                  {selectedWCIsForming ? (
                    <FormControl size="small" sx={{ minWidth: 180, flexShrink: 0 }}>
                      <InputLabel id={`frm-operator-${item.production_order_operation_id}`}>
                        Operatör
                      </InputLabel>
                      <Select
                        labelId={`frm-operator-${item.production_order_operation_id}`}
                        value={item.operator_user_id || ""}
                        label="Operatör"
                        onChange={(event) =>
                          handleOperatorChange(item.production_order_operation_id, event.target.value)
                        }
                      >
                        <MenuItem value="">
                          <em>Seçiniz...</em>
                        </MenuItem>
                        {formingOperators.map((operator) => (
                          <MenuItem key={operator.id} value={operator.id}>
                            {operator.name}{operator.employee_code ? ` (${operator.employee_code})` : ""}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}

                  {/* Miktar: tabak / tabaka hedefi (kayıt sonrası) */}
                  <Box sx={{ textAlign: "right", flexShrink: 0 }}>
                    <Typography variant="caption" display="block">
                      {Number(item.qty_done || 0).toLocaleString("tr-TR")} /
                      {Number(item.qty_target || 0).toLocaleString("tr-TR")}
                    </Typography>
                    {isCuttingCode(item.operation_code) && item.sheet_target_qty != null ? (
                      <Typography variant="caption" color="text.secondary" display="block">
                        Tabaka hedef: {Number(item.sheet_target_qty).toLocaleString("tr-TR")}
                      </Typography>
                    ) : null}
                  </Box>

                  {/* Kontroller */}
                  <Stack direction="column" spacing={0.25}>
                    <Tooltip title="Yukarı">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveUp(idx)}
                          disabled={idx === 0}
                          sx={{ p: 0.25 }}
                        >
                          ▲
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Aşağı">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleMoveDown(idx)}
                          disabled={idx === queue.length - 1}
                          sx={{ p: 0.25 }}
                        >
                          ▼
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Tooltip title="Kuyruktan çıkar">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleRemove(idx)}
                      sx={{ p: 0.5 }}
                    >
                      ✕
                    </IconButton>
                  </Tooltip>
                </Box>
              );
              })}
            </Stack>
            <ListPager page={currentQueuePage} total={queue.length} onChange={setQueuePage} />

            <Box mt={2}>
              <Button
                variant="contained"
                disabled={saving || loading}
                onClick={handleSave}
                sx={{ px: 2.5, py: 1, borderRadius: 2, textTransform: "none", fontWeight: 800, bgcolor: "#4f46e5", boxShadow: "none", "&:hover": { bgcolor: "#3730a3", boxShadow: "none" } }}
              >
                {saving ? "Kaydediliyor..." : "Kaydet"}
              </Button>
            </Box>
          </Paper>

         {/* Sağ: Eklenebilir İşler */}
          <Paper elevation={0} sx={{ flex: 1, p: { xs: 1.5, md: 2.25 }, minWidth: 0, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe", boxShadow: "0 12px 30px rgba(79, 70, 229, 0.08)" }}>
            <Typography variant="subtitle1" fontWeight={900} color="#312e81" mb={1.5}>
              Eklenebilir İşler ({filteredAvailable.length})
            </Typography>
            {!loading && available.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Bu iş merkezinde bekleyen iş yok.
              </Typography>
            )}
            {!loading && available.length > 0 && filteredAvailable.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Aramaya uyan iş bulunamadı.
              </Typography>
            )}
            <Stack spacing={1}>
              {visibleAvailable.map((op) => (
                <Box
                  key={op.production_order_operation_id}
                  sx={{
                    border: "1px solid",
                    borderColor: "#ddd6fe",
                    borderRadius: 2,
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={700} noWrap>
                      {op.customer_code || "—"} — {op.customer_name || "—"} — {op.order_no || "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {op.product_name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      Müşteri Ürün Kodu: {op.customer_product_code || "—"}
                    </Typography>
                    <Stack direction="row" spacing={0.5} mt={0.3}>
                      <Chip
                        label={op.operation_code || `OP${op.op_no}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: 12, height: 24 }}
                      />
                    </Stack>
                  </Box>
                  <Typography variant="caption" sx={{ flexShrink: 0, mr: 1 }}>
                    {Number(op.qty_target || 0).toLocaleString("tr-TR")}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => handleAdd(op)}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800, borderColor: "#818cf8", color: "#4338ca", "&:hover": { borderColor: "#4f46e5", bgcolor: "#eef2ff" } }}
                  >
                    + Ekle
                  </Button>
                </Box>
              ))}
            </Stack>
            <ListPager page={currentAvailablePage} total={filteredAvailable.length} onChange={setAvailablePage} />
          </Paper>
        </Stack>
      )}

      {/* Rapor: Seçili iş merkezi kuyruğu */}
      <Paper elevation={0} sx={{ p: { xs: 1.5, md: 2.5 }, borderRadius: 4, bgcolor: "#f5f3ff", border: "1px solid #c7d2fe", boxShadow: "0 12px 30px rgba(79, 70, 229, 0.08)", "& .MuiTableCell-head": { bgcolor: "#ede9fe", color: "#3730a3", borderColor: "#ddd6fe" }, "& .MuiTableCell-root": { borderColor: "#e5e7eb" } }}>
        <Typography variant="subtitle1" fontWeight={900} color="#312e81" mb={2}>
          {selectedWCId ? "Seçili İş Merkezi Kuyruk Raporu" : "Tüm İş Merkezleri Kuyruk Raporu"}
        </Typography>
        {!selectedWCId ? (
          allQueuesLimited.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Henüz hiçbir iş merkezine iş atanmamış.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {allQueuesLimited.map((wc) => (
                <Box key={wc.work_center_id}>
                  <Typography variant="body1" fontWeight={700} gutterBottom>
                    {wc.work_center_name}
                    {wc.work_center_code ? ` (${wc.work_center_code})` : ""}
                    {isCuttingCode(wc.work_center_name) && (
                      <Chip
                        label="sıra serbest"
                        size="small"
                        color="info"
                        sx={{ ml: 1, fontSize: 12, height: 24 }}
                      />
                    )}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>İş Emri</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Müşteri Ürün Kodu</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Ürün</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Operasyon</TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>Durum</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {wc.items.map((item, idx) => (
                          <TableRow
                            key={`${item.production_order_operation_id}:${item.changed_at || idx}`}
                            sx={item.op_status === "finished" ? { bgcolor: "#f0fdf4" } : {}}
                          >
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {item.order_no}
                              </Typography>
                            </TableCell>
                            <TableCell>{item.customer_product_code || ""}</TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                {item.product_name}
                                {item.product_variant_name ? ` — ${item.product_variant_name}` : ""}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography variant="body2">
                                  {item.operation_code || `OP${item.op_no}`}
                                </Typography>
                                {isCuttingCode(item.operation_code) && (
                                  <Chip
                                    label="sıra serbest"
                                    size="small"
                                    color="info"
                                    sx={{ fontSize: 12, height: 24 }}
                                  />
                                )}
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={STATUS_LABELS[item.op_status] || item.op_status}
                                color={STATUS_COLORS[item.op_status] || "default"}
                                size="small"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Stack>
          )
        ) : !selectedWCReport || selectedReportItems.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Seçili iş merkezi için kuyruk kaydı yok.
          </Typography>
        ) : (
          <Box>
            <Typography variant="body1" fontWeight={700} gutterBottom>
              {selectedWCReport.work_center_name}
              {selectedWCReport.work_center_code ? ` (${selectedWCReport.work_center_code})` : ""}
              {isCuttingCode(selectedWCReport.work_center_name) && (
                <Chip
                  label="sıra serbest"
                  size="small"
                  color="info"
                  sx={{ ml: 1, fontSize: 12, height: 24 }}
                />
              )}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>İş Emri</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Müşteri Ürün Kodu</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Ürün</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Operasyon</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Durum</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedReportItems.map((item, idx) => (
                    <TableRow
                      key={`${item.production_order_operation_id}:${item.changed_at || idx}`}
                      sx={item.op_status === "finished" ? { bgcolor: "#f0fdf4" } : {}}
                    >
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>
                          {item.order_no}
                        </Typography>
                      </TableCell>
                      <TableCell>{item.customer_product_code || ""}</TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                          {item.product_name}
                          {item.product_variant_name ? ` — ${item.product_variant_name}` : ""}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="body2">
                            {item.operation_code || `OP${item.op_no}`}
                          </Typography>
                          {isCuttingCode(item.operation_code) && (
                            <Chip
                              label="sıra serbest"
                              size="small"
                              color="info"
                              sx={{ fontSize: 12, height: 24 }}
                            />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_LABELS[item.op_status] || item.op_status}
                          color={STATUS_COLORS[item.op_status] || "default"}
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Paper>
    </Stack>
  );
}
